/**
 * User Files Types — Persistent File Library
 *
 * Single source of truth for user-file management types.
 * Used by server, frontend, and sandbox.
 */
import { isLibraryFilePath } from '../sandbox/lifetimes.js';
import type { z } from 'zod';
import type { UserFileReadyPayloadSchema } from '../schemas/forwarded-payloads.schema.js';

// =============================================================================
// Enums / Unions
// =============================================================================

export type FileSource = 'user' | 'agent';
export type FileCategory = 'document' | 'image' | 'video' | 'audio' | 'data' | 'other';
export type SyncStatus = 'pending' | 'synced' | 'error';
export type FileVisibility = 'private' | 'link';

// =============================================================================
// Folder (unlimited nesting via parent_id adjacency list)
// =============================================================================

export interface UserFolder {
  id: string;
  parentId: string | null;
  name: string;
  position: number;
  children?: UserFolder[];
}

// =============================================================================
// File
// =============================================================================

export interface UserFile {
  id: string;
  filename: string;
  mimeType: string | null;
  sizeBytes: number | null;
  source: FileSource;
  category: FileCategory;
  syncStatus: SyncStatus;
  taskId: string | null;
  folderId: string | null;
  createdAt: string;
  downloadUrl?: string;
  /** Set only when a precomputed thumbnail is ready — a `/v/{id}?thumb=1`
   *  proxy URL serving a stored, resized object (never a live transform).
   *  Undefined while pending/failed/skipped; callers fall back to
   *  `downloadUrl`. */
  thumbUrl?: string;
  /** Pixel dimensions — image/video only, set once the render lane has
   *  probed the file (mirrors task_artifacts.width/height). Undefined for
   *  every other category, or before the probe completes. */
  width?: number | null;
  height?: number | null;
  /** Seconds — video only (mirrors task_artifacts.duration_s). */
  durationS?: number | null;
  textPreview?: string;
  visibility: FileVisibility;
}

// =============================================================================
// API Response
// =============================================================================

export interface UserFilesListing {
  folders: UserFolder[];
  files: UserFile[];
  total: number;
}

// =============================================================================
// Sync Payload (client → server atomic save)
// =============================================================================

export interface UserFilesSyncPayload {
  folders: {
    id: string;
    parentId: string | null;
    name: string;
    position: number;
  }[];
  fileAssignments: {
    fileId: string;
    folderId: string | null;
  }[];
}


// =============================================================================
// Mutation Payloads (client → server)
// =============================================================================

export interface UserCreateFilePayload {
  filename: string;
  content: string;
  mimeType: string;
  folderId: string | null;
}

/**
 * PATCH /api/files/:fileId body. Both keys are optional but the request must
 * carry at least one; anything else in the body is rejected rather than
 * dropped, so a caller never gets `success` for work that did not happen.
 * `folderId: null` means the library root.
 */
export interface UserFileUpdatePayload {
  filename?: string;
  folderId?: string | null;
}

export interface UserFileVisibilityPayload {
  visibility: FileVisibility;
}

export interface UserFilesBatchDeletePayload {
  fileIds: string[];
}

export interface UserFilesBatchMovePayload {
  fileIds: string[];
  folderId: string | null;
}

// =============================================================================
// Socket Event Payloads
// =============================================================================

/** files:file_ready, derived from its registry schema (one author; `base64` is the fallback, prefer cdnUrl). */
export type UserFileReadyPayload = z.infer<typeof UserFileReadyPayloadSchema>;

// =============================================================================
// Reserved root-folder names
// =============================================================================

/**
 * The system folders of the Files root (plus the breadcrumb root label). A user
 * folder at the ROOT level must not collide with them: the surface would show
 * two folders with one name, and an agent folder reference would turn ambiguous.
 * Subfolders are unaffected — inside "vestra" a "media" folder is unambiguous.
 *
 * Lives in shared because both the web create/rename guard and the server's
 * volume-path placement rail have to agree on the list.
 */
const RESERVED_ROOT_FOLDER_NAMES = ['creations', 'uploaded', 'shared with me', 'media', 'temp', 'this task', 'my files'] as const;

export function isReservedFolderName(name: string): boolean {
  return (RESERVED_ROOT_FOLDER_NAMES as readonly string[]).includes(name.trim().toLowerCase());
}

/**
 * Would a file or folder called `name`, inside the folder at `parentPath` ('' for the root of
 * the library, else '/Brand/Q3'), sit at a path the Files library never lists? A folder IS
 * `files/<its path>` on the box, so a name like that is created and then silently never syncs
 * (task ddf43b7f): `apps` at the root (the app home), a build or dependency tree (`dist`,
 * `node_modules`) or a hidden name at ANY depth. Asked of the library's own rule, never a
 * second list, by every create and rename on the server and in the web. The name is case-folded
 * as the drawer names are: a desktop box's disk does not tell `Apps` from `apps`.
 */
export function isRefusedLibraryName(parentPath: string, name: string): boolean {
  return !isLibraryFilePath(`files${parentPath}/${name.trim().toLowerCase()}`);
}

/** Why isRefusedLibraryName said no, in words a person and an agent both act on. */
export function libraryNameRefusal(name: string): string {
  return `"${name.trim()}" can't be used here: the Files library doesn't keep hidden files or folders (names starting with "."), build or dependency folders like dist or node_modules, or a folder called "apps" at the top. Pick another name.`;
}
